import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  contactDistributionPollingIntervalAtom,
  contactDistributionProcessesAtom,
  contactDistributionQueuedProcessesAtom,
  removeContactDistributionProcessAtom,
  updateContactDistributionProcessAtom,
} from '@/src/atoms/contactDistribution';
import { EnhancedContactDistributionReport } from '@/src/infrastructure/interfaces/IContactDistributionBulk';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
export const useContactDistributionPolling = () => {
  const [processes] = useAtom(contactDistributionProcessesAtom);
  const [queuedProcesses, setQueuedProcesses] = useAtom(contactDistributionQueuedProcessesAtom);
  const [, setPollingInterval] = useAtom(contactDistributionPollingIntervalAtom);
  const [, updateProcess] = useAtom(updateContactDistributionProcessAtom);
  const [, removeProcess] = useAtom(removeContactDistributionProcessAtom);
  const service = useService();
  const navigate = useNavigate();


  useEffect(() => {
    if (processes.length === 0) return;

    const poll = async () => {
      const updatedProcesses: (EnhancedContactDistributionReport | null)[] = await Promise.all(
        processes.filter(process => process.status !== 'completed' && process.status !== 'failed').map(async (process) => {
          try {
            const progress = await service.getContactDistributionProgress(process.processId);
            return progress;
          } catch (error) {
            console.error('Error polling process progress:', error);
            return process;
          }
        })
      );

      const queuedProcessesToAdd = await Promise.all(queuedProcesses.map(async (processId) => {
        try {
          const progress = await service.getContactDistributionProgress(processId);
          return progress;
        } catch (error) {
          console.error('Error polling process progress:', error);
          return null;
        }
      }));

      const processesQueuedAlreadyStarted = queuedProcessesToAdd.filter(process => process !== null);
      // Remove processes that are already started from the list of queued processes
      setQueuedProcesses(queuedProcesses.filter(processId => !processesQueuedAlreadyStarted.some(process => process?.processId === processId)));

      const filteredUpdatedProcesses = [...updatedProcesses, ...processesQueuedAlreadyStarted].filter(process => process !== null);

      // Update processes
      filteredUpdatedProcesses.forEach(process => {
        updateProcess(process as EnhancedContactDistributionReport);
      });
    };

    const pollInterval = setInterval(() => {
      void poll();
    }, 2000);

    setPollingInterval(pollInterval);

    return () => {
      clearInterval(pollInterval);
      setPollingInterval(null);
    };
  }, [processes, service, updateProcess, removeProcess, setPollingInterval, queuedProcesses, setQueuedProcesses]);

  const onClose = (processId: string) => {
    removeProcess(processId);
  };

  const onGoTo = (processId: string) => {
    const prefixGroupName = processes.find(process => process.processId === processId)?.request.prefixGroupName;
    removeProcess(processId);
    if (prefixGroupName) {
      navigate(`/v2/contacts/groups?filterSearch=${prefixGroupName}`);
    } else {
      navigate(`/v2/contacts/groups`);
    }
  };

  return { onClose, onGoTo };

};
